import * as React from 'react'

import { Box, Text, ToggleButton } from '@factorialco/deprecated-design-system'
import { CreateAttendanceEditTimesheetRequestMutationVariables } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TimePicker from 'modules/core/components/TimePicker'
import { useFormContext } from 'modules/core/hooks/useForm'
import { convertDate } from 'modules/core/lib/dates'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import StartsNextDayCheckbox from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/StartsNextDayCheckbox'
import { locationTypeOptions } from 'modules/attendance/lib/locationType'
import { DEFAULT_CLOCK } from 'modules/attendance/lib/utils'

interface Props {
  isCreateShiftRequest: boolean
  isDeleteRequest?: boolean
  hasBreaksEnabled?: boolean
  hasLocationEnabled?: boolean
  dayBreaksOptions?: { label: string; value: number }[]
  showStartsNextDayCheckbox?: boolean
  date: moment.Moment
}

const Form = ({
  isCreateShiftRequest,
  isDeleteRequest,
  hasBreaksEnabled,
  hasLocationEnabled,
  dayBreaksOptions,
  showStartsNextDayCheckbox,
  date,
}: Props) => {
  const form = useFormContext<CreateAttendanceEditTimesheetRequestMutationVariables>()
  const hasPaidBreaks = hasFeature('time_tracking_paid_breaks')

  const startsNextDay = form.values.date != form.values.referenceDate

  return (
    <>
      <Box>
        <Text.Normal>{i18n.t('clock_in.edit_timesheet_requests.modal.date')}</Text.Normal>
        <Text.Normal weight="bold">
          {form.values.referenceDate ? convertDate(form.values.referenceDate, 'backend', 'ui') : ''}
        </Text.Normal>
      </Box>
      {isCreateShiftRequest && hasBreaksEnabled && (
        <Box paddingTop="s16">
          <Box flexDirection="row" gap="s8">
            <ToggleButton
              toggled={form.values.workable ?? false}
              onClick={() =>
                form.setValues({
                  ...form.values,
                  workable: true,
                  timeSettingsBreakConfigurationId: null,
                })
              }
              label={i18n.t('time_tracking.table.body.category.work')}
            />
            <ToggleButton
              toggled={!form.values.workable}
              onClick={() => form.setFieldValue('workable', false)}
              label={i18n.t('time_tracking.break')}
            />
          </Box>
        </Box>
      )}
      {!form.values.workable && hasPaidBreaks && !!dayBreaksOptions?.length && (
        <Box paddingTop="s16">
          <SelectField
            label={i18n.t('clock_in.edit_timesheet_requests.modal.break_name')}
            options={dayBreaksOptions || []}
            onChange={(value) =>
              form.setFieldValue('timeSettingsBreakConfigurationId', parseInt(value))
            }
            readonly={isDeleteRequest}
          />
        </Box>
      )}
      <Box flexDirection="row" alignItems="center" paddingTop="s16">
        <Box borderRadius={{ all: 'abs008' }}>
          <TimePicker
            value={form.values.clockIn || DEFAULT_CLOCK}
            onChange={(value) => form.setFieldValue('clockIn', value)}
            readonly={isDeleteRequest}
          />
        </Box>
        <Box padding="s6">
          <Text.Normal color="grey600">–</Text.Normal>
        </Box>
        <Box borderRadius={{ all: 'abs008' }}>
          <TimePicker
            value={form.values.clockOut || DEFAULT_CLOCK}
            onChange={(value) => form.setFieldValue('clockOut', value)}
            readonly={isDeleteRequest}
          />
        </Box>
      </Box>
      {showStartsNextDayCheckbox && (
        <Box paddingTop="s16">
          <StartsNextDayCheckbox
            checked={startsNextDay}
            onClick={() => {
              const newStartsNextDay = !startsNextDay
              const newDate = newStartsNextDay ? date.clone().add(1, 'days') : date

              form.setValues({
                ...form.values,
                date: dateToString(newDate, 'backend'),
              })
            }}
          ></StartsNextDayCheckbox>
        </Box>
      )}
      {hasLocationEnabled && (
        <Box paddingTop="s16">
          <SelectField
            label={i18n.t('clock_in.locations.modal.title')}
            options={locationTypeOptions()}
            onChange={(value) => form.setFieldValue('locationType', value)}
            readonly={isDeleteRequest}
          />
        </Box>
      )}
      <Box paddingTop="s16">
        <TextArea
          label={i18n.t('clock_in.edit_timesheet_requests.modal.observations_label')}
          value={form.values.observations || ''}
          onChange={(observations) => form.setFieldValue('observations', observations)}
          readonly={isDeleteRequest}
        />
      </Box>
    </>
  )
}

export default Form
